/**
 * el-table 自适应兜底（全局注入，main.js 里 Vue.use）
 *
 * 背景：Element UI 的列宽计算（element-ui/packages/table/src/table-layout.js updateColumnsWidth）
 * 分两条路：
 *   - 只要有列没写 width（弹性列）且 fit=true，就把容器多出来的宽度分给弹性列，表格正好铺满；
 *   - 所有列都写了 width 时，bodyWidth 直接等于各列宽之和，容器多出来的宽度不分配。
 * 本项目所有列表页都是第二种写法（每列都写了 width）。于是只要容器比列宽之和还宽
 * —— 大分辨率、高 DPI 缩放（Windows 150% 下 2240 物理宽只有 ~1490 CSS 宽，容器 ~1230）、
 * 浏览器缩小、侧边栏折叠后都会 —— 表格主体右侧空出一块，而 fixed="right" 的操作列是钉在
 * 容器右边缘的，于是就出现用户截图里的样子：**操作列在界面上出现两次**
 * —— 一次在表格主体末尾（固定列在主体里也渲染一份，本来正好被覆盖层盖住，容器一宽就露出来，
 * 而且主表那份的表头是空的），一次钉在容器右边缘，两者中间是一大片空白。
 * 实测（无头 Edge 量 getBoundingClientRect）：2202 容器 / Σ列宽 1815 时表格主体到 1817 就结束，
 * 固定操作列却从 2101 起，中间 284px 全空、两列重影；单纯 doLayout 修不掉，因为重算出来还是
 * 1815（不重算 2202 的原因就在 else 分支：bodyWidth = bodyMinWidth，压根不看容器）。
 *
 * 这里做两件事：
 *   1. 布局时若所有列都写了 width，把所有「非固定数据列」改成弹性列（minWidth = 原 width，
 *      width = null），交给 Element 自己的 fit 逻辑分配剩余宽度，表格正好铺满容器、
 *      固定列严丝合缝。用「全部数据列按比例分摊」而不是「只让最后一列吃掉」：
 *      Element 对多弹性列是按 minWidth 等比分摊的（table-layout.js:158-173），
 *      这样宽屏下是整张表等比宽松，而不是某一列被拉成一个巨大的空列
 *      —— 小列宽的表（如 kycConfig Σ670、regConfig Σ932）在 1230 容器下，若只撑最后一列，
 *      58px 的「操作」列会变成 300~600px，按钮孤零零挂在左边，很难看。
 *      selection / index / expand 这类结构列（type !== 'default'）与固定列不参与分摊，保持原宽。
 *      已经有弹性列的表直接跳过。
 *   2. 容器宽度变化后重算：窗口 resize（改分辨率 / 浏览器缩放 / 拖窗口）+ 每张表自己的
 *      ResizeObserver（侧边栏折叠、弹窗由隐藏变可见这类不动窗口的宽度变化）。
 *      Element 自己虽然也 addResizeListener，但实测容器从 1200 加宽到 2200 时它没有重算
 *      （表格仍停在 1815），所以不能只依赖它。
 *
 * 安全性：列宽之和大于容器时 Element 走「有横向滚动条」分支，所有弹性列 realWidth 退回
 * minWidth（= 原来的 width），布局与改造前逐列完全一致 —— 实测收窄到 700 时表头/表体 16 列
 * 仍是 45/110/120/…/100，与改造前分毫不差；容器 602 / Σ670 时也保持原宽 + 滚动条。
 * 未写 width 的表（本来就有弹性列）完全不受影响。纯布局兜底，不碰业务数据与请求。
 */

const liveTables = new Set()
const observers = new WeakMap()

/** 把所有非固定数据列改成弹性列；已经有弹性列（或没有可改的列）时不动 */
function flexColumns(table) {
  const states = table.store && table.store.states
  if (!states) return
  const columns = (states.columns || []).filter(column => !column.isColumnGroup)
  if (!columns.length) return
  // 有没写 width 的列 → Element 自己就会铺满容器
  if (columns.some(column => typeof column.width !== 'number')) return
  const fixedColumns = (states.fixedColumns || []).concat(states.rightFixedColumns || [])
  const targets = columns.filter(column =>
    column.type === 'default' &&
    typeof column.width === 'number' &&
    fixedColumns.indexOf(column) === -1
  )
  if (!targets.length) return
  targets.forEach(column => {
    column.minWidth = column.width
    column.width = null
    column.realWidth = null
  })
}

function relayout(table) {
  try {
    flexColumns(table)
    if (typeof table.doLayout === 'function') table.doLayout()
  } catch (e) {
    // 兜底逻辑失败不能连累页面
  }
}

function relayoutAll() {
  liveTables.forEach(relayout)
}

export default {
  install(Vue) {
    if (typeof window !== 'undefined') {
      // 拖窗口 / 改分辨率 / 浏览器缩放：resize 会连发，简单防抖即可
      let timer = null
      window.addEventListener('resize', () => {
        if (timer) clearTimeout(timer)
        timer = setTimeout(() => {
          timer = null
          relayoutAll()
        }, 120)
      })
    }

    Vue.mixin({
      mounted() {
        if (!this.$options || this.$options.name !== 'ElTable') return
        const table = this
        liveTables.add(table)
        // 列在子组件 created 时注册，父组件 mounted 时已经齐了；再等一个 tick 是给
        // 「列由 v-for 动态生成」的表留出注册时间（这类表本来就没写死 width，不依赖本兜底）
        this.$nextTick(() => relayout(table))
        if (typeof ResizeObserver === 'undefined' || !this.$el) return
        let lastWidth = this.$el.clientWidth
        const observer = new ResizeObserver(() => {
          const width = table.$el ? table.$el.clientWidth : lastWidth
          if (width === lastWidth) return
          lastWidth = width
          relayout(table)
        })
        observer.observe(this.$el)
        observers.set(table, observer)
      },
      beforeDestroy() {
        liveTables.delete(this)
        const observer = observers.get(this)
        if (observer) {
          observer.disconnect()
          observers.delete(this)
        }
      }
    })
  }
}
